import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CommonUtilityLib, StorageUtilityLib, USER_SESSION_TOKEN, UserAccessService, UserSession } from '@thehubz/client/core';
import { AuthApiService } from '@thehubz/client/data-access-api';
import { ANONYMOUS_NAME } from '@thehubz/shared/model';
import { Observable, map, of } from 'rxjs';

@Injectable()
export class AnonymousAuthService {
  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private router: Router,
    private storageUtility: StorageUtilityLib,
    private authApiService: AuthApiService,
    private userAccess: UserAccessService,
    private commonUtilityLib: CommonUtilityLib,
  ) {}

  checkAndLoginAnonymously(): Observable<UserSession> {
    const userSession = this.storageUtility.getLocalStorageNullable<UserSession>(this.userSessionToken);
    if (!userSession || !userSession.access_token) {
      return this.loginAnonymously();
    }
    return of(userSession);
  }

  loginAnonymously(): Observable<UserSession> {
    return this.authApiService
      .authControllerLogin({
        username: ANONYMOUS_NAME,
        password: ANONYMOUS_NAME,
      })
      .pipe(
        map((data) => {
          if (data?.access_token) {
            const userSession: UserSession = {
              access_token: data.access_token,
              expires_at: data.expires_at,
              userData: data.user,
              userAccessData: {
                globalRoles: data.accessConfig.globalRoles,
                accessRoles: data.accessConfig.accessRoles as unknown as [string, string[]][],
              },
            };
            this.storageUtility.setLocalStorage(this.userSessionToken, userSession);
            return userSession;
          } else {
            throw new Error('No access token');
          }
        }),
      );
  }

  logout(navigateTo?: string) {
    this.authApiService.authControllerLogout().subscribe({
      complete: async () => {
        this.clearUserSession();
        if (navigateTo) {
          await this.router.navigate([navigateTo]);
        } else {
          await this.router.navigate(['/']);
          window.location.reload();
        }
      },
      error: async () => {
        this.clearUserSession();
        await this.router.navigate(['/']);
        window.location.reload();
      },
    });
  }

  private clearUserSession() {
    this.commonUtilityLib.routingObjectSetter({});
    this.storageUtility.clearLocalStorage(this.userSessionToken);
    this.storageUtility.clearAllSessionStorage();
    this.userAccess.clear();
  }
}
